import React, { useEffect, useRef } from 'react';
import {
  Animated,
  TouchableWithoutFeedback,
  ViewStyle,
  Easing,
} from 'react-native';
import { green, disabledGrey, white } from '@/shared/styles/colors';

// NOTE: for use on web to match design style

interface Props {
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
}

const SWITCH_WIDTH = 60;
const SWITCH_HEIGHT = 32;
const THUMB_SIZE = 22;
const PADDING = 2;
const ON_COLOR = green;
const OFF_COLOR = disabledGrey;

export default function IOSLikeSwitch({
  value,
  onValueChange,
  disabled = false,
}: Props) {
  const anim = useRef(new Animated.Value(value ? 1 : 0)).current;

  // Animate when value changes
  useEffect(() => {
    Animated.timing(anim, {
      toValue: value ? 1 : 0,
      duration: 200,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start();
  }, [value, anim]);

  const translateX = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [2, SWITCH_WIDTH - THUMB_SIZE - 6],
  });

  // Track color tween
  const backgroundColor = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [OFF_COLOR, ON_COLOR],
  });

  return (
    <TouchableWithoutFeedback
      onPress={() => !disabled && onValueChange(!value)}
      disabled={disabled}
    >
      <Animated.View
        style={[
          { ...trackStyle },
          {
            backgroundColor,
          },
        ]}
      >
        <Animated.View
          style={[
            { ...thumbStyle },
            {
              transform: [{ translateX }],
            },
          ]}
        />
      </Animated.View>
    </TouchableWithoutFeedback>
  );
}

const trackStyle: ViewStyle = {
  width: SWITCH_WIDTH,
  height: SWITCH_HEIGHT,
  borderRadius: SWITCH_HEIGHT / 2,
  justifyContent: 'center',
  padding: PADDING,
};

const thumbStyle: ViewStyle = {
  backgroundColor: white,
  width: THUMB_SIZE,
  height: THUMB_SIZE,
  borderRadius: THUMB_SIZE / 2,
};
